import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, Injector, PLATFORM_ID, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SessionTimeoutStateEnum } from '../enums';
import { JwtPayloadModel } from '../models';
import { AuthenticationService } from './authentication.service';
import { UserAccountService } from './user-account.service';

/**
 * Warning thresholds for session timeout (in seconds)
 */
const WARNING_THRESHOLD_SECONDS = 5 * 60; // 5 minutes before expiry
const CRITICAL_THRESHOLD_SECONDS = 1 * 60; // 1 minute before expiry
const CHECK_INTERVAL_MS = 30_000; // Check every 30 seconds

/**
 * Service to monitor JWT expiration and warn users before session timeout.
 * Provides signals for UI components to display warnings and handles automatic logout.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #injector = inject(Injector);
  readonly #userAccountService = inject(UserAccountService);
  readonly #authService = inject(AuthenticationService);
  readonly #translateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  // Lazy-loaded notification service to avoid SSR issues
  #notificationService: NzNotificationService | null = null;

  // Internal state
  readonly #currentState = signal<SessionTimeoutStateEnum>(SessionTimeoutStateEnum.ACTIVE);
  readonly #secondsUntilExpiry = signal<number | null>(null);
  readonly #isMonitoring = signal(false);
  readonly #stopMonitoring$ = new Subject<void>();

  // Active notification reference for dismissal
  #warningNotificationRef: NzNotificationRef | null = null;

  // Exposed read-only signals
  /** Current session state (active, warning, critical, expired) */
  readonly state = this.#currentState.asReadonly();

  /** Seconds remaining until session expires (null if not monitoring or no token) */
  readonly secondsUntilExpiry = this.#secondsUntilExpiry.asReadonly();

  /** Whether the service is actively monitoring the session */
  readonly isMonitoring = this.#isMonitoring.asReadonly();

  /** Human-readable time remaining (e.g., "4:32") */
  readonly timeRemaining = computed(() => {
    const seconds = this.#secondsUntilExpiry();
    if (seconds === null || seconds <= 0) return null;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  });

  /** Whether a warning should be shown */
  readonly showWarning = computed(() => {
    const state = this.#currentState();
    return state === SessionTimeoutStateEnum.WARNING || state === SessionTimeoutStateEnum.CRITICAL;
  });

  constructor() {
    // Only run on browser - SSR should not monitor sessions
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    // Register effect for automatic session monitoring when JWT changes
    // Use untracked() to prevent infinite loops from signal writes in startMonitoring/stopMonitoring
    effect(() => {
      const jwtDetails = this.#userAccountService.userJWTDetails();
      untracked(() => {
        if (jwtDetails) {
          this.startMonitoring(jwtDetails);
        } else {
          this.stopMonitoring();
        }
      });
    });
  }

  /**
   * Get notification service lazily to avoid SSR issues
   */
  private getNotificationService(): NzNotificationService | null {
    if (!isPlatformBrowser(this.#platformId)) {
      return null;
    }
    if (!this.#notificationService) {
      try {
        this.#notificationService = this.#injector.get(NzNotificationService);
      } catch {
        // Notification service not available
        return null;
      }
    }
    return this.#notificationService;
  }

  /**
   * Start monitoring session expiration
   */
  private startMonitoring(jwtDetails: JwtPayloadModel): void {
    if (this.#isMonitoring()) {
      this.stopMonitoring();
    }

    this.#isMonitoring.set(true);

    // Check immediately
    this.checkExpiration(jwtDetails);

    // Then check periodically
    interval(CHECK_INTERVAL_MS)
      .pipe(
        takeUntil(this.#stopMonitoring$),
        takeUntilDestroyed(this.#destroyRef),
        filter(() => this.#isMonitoring()),
      )
      .subscribe(() => {
        // Re-read JWT details in case they were refreshed
        const currentJwtDetails = this.#userAccountService.userJWTDetails();
        if (currentJwtDetails) {
          this.checkExpiration(currentJwtDetails);
        }
      });
  }

  /**
   * Stop monitoring session expiration
   */
  private stopMonitoring(): void {
    this.#stopMonitoring$.next();
    this.#isMonitoring.set(false);
    this.#currentState.set(SessionTimeoutStateEnum.ACTIVE);
    this.#secondsUntilExpiry.set(null);
    this.dismissWarningNotification();
  }

  /**
   * Check the current expiration status and update state
   */
  private checkExpiration(jwtDetails: JwtPayloadModel): void {
    const expirationTimestamp = this.parseExpirationTime(jwtDetails.exp);
    if (!expirationTimestamp) {
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsRemaining = expirationTimestamp - nowSeconds;

    this.#secondsUntilExpiry.set(Math.max(0, secondsRemaining));

    // Determine state based on time remaining
    const previousState = this.#currentState();
    let newState: SessionTimeoutStateEnum;

    if (secondsRemaining <= 0) {
      newState = SessionTimeoutStateEnum.EXPIRED;
    } else if (secondsRemaining <= CRITICAL_THRESHOLD_SECONDS) {
      newState = SessionTimeoutStateEnum.CRITICAL;
    } else if (secondsRemaining <= WARNING_THRESHOLD_SECONDS) {
      newState = SessionTimeoutStateEnum.WARNING;
    } else {
      newState = SessionTimeoutStateEnum.ACTIVE;
    }

    this.#currentState.set(newState);

    // Handle state transitions
    if (previousState !== newState) {
      this.handleStateTransition(previousState, newState, secondsRemaining);
    }
  }

  /**
   * Parse the expiration time from JWT (can be string or number)
   */
  private parseExpirationTime(exp: string | number): number | null {
    if (typeof exp === 'number') {
      return exp;
    }
    if (typeof exp === 'string') {
      const parsed = parseInt(exp, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  /**
   * Handle state transitions and show appropriate notifications
   */
  private handleStateTransition(from: SessionTimeoutStateEnum, to: SessionTimeoutStateEnum, secondsRemaining: number): void {
    // Dismiss any existing warning when transitioning away from warning states
    if ((from === SessionTimeoutStateEnum.WARNING || from === SessionTimeoutStateEnum.CRITICAL) && to === SessionTimeoutStateEnum.ACTIVE) {
      this.dismissWarningNotification();
      return;
    }

    // Handle expired state
    if (to === SessionTimeoutStateEnum.EXPIRED) {
      this.dismissWarningNotification();
      this.handleSessionExpired();
      return;
    }

    // Show warning notification
    if (to === SessionTimeoutStateEnum.WARNING) {
      this.showWarningNotification(secondsRemaining);
    } else if (to === SessionTimeoutStateEnum.CRITICAL) {
      this.showCriticalNotification(secondsRemaining);
    }
  }

  /**
   * Show a warning notification (5 minutes remaining)
   */
  private showWarningNotification(secondsRemaining: number): void {
    this.dismissWarningNotification();

    const notification = this.getNotificationService();
    if (!notification) return;

    const minutes = Math.ceil(secondsRemaining / 60);
    const message = this.#translateService.instant('session.warning_message', { minutes });
    const title = this.#translateService.instant('session.warning_title');

    this.#warningNotificationRef = notification.warning(title, message, {
      nzDuration: 0, // Don't auto-dismiss
      nzKey: 'session-timeout-warning',
      nzPlacement: 'topRight',
    });
  }

  /**
   * Show a critical notification (1 minute remaining)
   */
  private showCriticalNotification(secondsRemaining: number): void {
    this.dismissWarningNotification();

    const notification = this.getNotificationService();
    if (!notification) return;

    const message = this.#translateService.instant('session.critical_message', { seconds: secondsRemaining });
    const title = this.#translateService.instant('session.critical_title');

    this.#warningNotificationRef = notification.error(title, message, {
      nzDuration: 0, // Don't auto-dismiss
      nzKey: 'session-timeout-warning',
      nzPlacement: 'topRight',
    });
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpired(): void {
    const notification = this.getNotificationService();
    if (notification) {
      const message = this.#translateService.instant('session.expired_message');
      const title = this.#translateService.instant('session.expired_title');

      notification.error(title, message, {
        nzDuration: 5000,
        nzPlacement: 'topRight',
      });
    }

    // Logout after showing the notification
    setTimeout(() => {
      this.#authService.logout();
    }, 1000);
  }

  /**
   * Dismiss the warning notification if active
   */
  private dismissWarningNotification(): void {
    if (this.#warningNotificationRef) {
      const notification = this.getNotificationService();
      if (notification) {
        notification.remove(this.#warningNotificationRef.messageId);
      }
      this.#warningNotificationRef = null;
    }
  }

  /**
   * Manually refresh the session (calls the token refresh endpoint)
   * Call this when user clicks "Extend Session" button
   */
  async refreshSession(): Promise<boolean> {
    try {
      const currentJWT = this.#userAccountService.userJWT();
      if (!currentJWT?.refreshToken) {
        return false;
      }

      // The actual refresh is handled by the HTTP interceptor/auth service
      // Here we just trigger a refresh by calling the refresh endpoint
      const refreshed = await new Promise<boolean>((resolve) => {
        this.#authService.refreshToken({ refreshToken: currentJWT.refreshToken }).subscribe({
          next: async (newTokens) => {
            await this.#userAccountService.setUserAccount(newTokens);
            this.dismissWarningNotification();

            const notification = this.getNotificationService();
            if (notification) {
              notification.success(
                this.#translateService.instant('session.extended_title'),
                this.#translateService.instant('session.extended_message'),
                { nzDuration: 3000 },
              );
            }

            resolve(true);
          },
          error: () => {
            resolve(false);
          },
        });
      });

      return refreshed;
    } catch {
      return false;
    }
  }
}
